import { cache } from 'react'
import type { Payload, Where } from 'payload'

import type { Discount } from '@/payload-types'

export function isDiscountActive(discount: Discount, now: Date = new Date()): boolean {
  const isActive = discount.is_active ?? true
  if (!isActive) return false

  const startDate = discount.start_date ? new Date(discount.start_date) : null
  const expirationDate = discount.expiration_date ? new Date(discount.expiration_date) : null

  if (startDate && startDate > now) return false
  if (expirationDate && expirationDate < now) return false
  if (discount.max_uses && discount.max_uses > 0 && (discount.current_uses || 0) >= discount.max_uses) {
    return false
  }

  return true
}

export function buildActiveDiscountsWhere(
  now: Date = new Date(),
  extraConditions: Where[] = [],
): Where {
  const nowISO = now.toISOString()
  return {
    and: [
      {
        is_active: {
          equals: true,
        },
      },
      {
        or: [
          {
            start_date: {
              equals: null,
            },
          },
          {
            start_date: {
              exists: false,
            },
          },
          {
            start_date: {
              less_than_equal: nowISO,
            },
          },
        ],
      },
      {
        or: [
          {
            expiration_date: {
              equals: null,
            },
          },
          {
            expiration_date: {
              exists: false,
            },
          },
          {
            expiration_date: {
              greater_than_equal: nowISO,
            },
          },
        ],
      },
      ...extraConditions,
    ],
  }
}

export type DiscountScope =
  | {
      discount: Discount
      scope: 'all'
      productIds: number[]
      categoryIds: number[]
    }
  | {
      discount: Discount
      scope: 'product'
      productIds: number[]
      categoryIds: number[]
    }
  | {
      discount: Discount
      scope: 'category'
      productIds: number[]
      categoryIds: number[]
    }

type RelationshipValue = (number | { id: number })[] | null | undefined

export function extractIdsFromRelationship(value: RelationshipValue): number[] {
  if (!value || value.length === 0) return []
  return value
    .map((entry) => {
      if (typeof entry === 'number') return entry
      if (typeof entry === 'object' && entry !== null && typeof entry.id === 'number') return entry.id
      return null
    })
    .filter((id): id is number => typeof id === 'number')
}

export async function resolveDiscountScope(
  payload: Payload,
  discountId: number,
): Promise<DiscountScope | null> {
  if (!discountId || Number.isNaN(discountId)) return null

  let discount: Discount | null = null
  try {
    discount = (await payload.findByID({
      collection: 'discounts',
      id: discountId,
      depth: 0,
    })) as Discount
  } catch (err) {
    return null
  }

  if (!discount) return null

  const now = new Date()
  if (!isDiscountActive(discount, now)) return null

  if (discount.appliesTo === 'all') {
    return { discount, scope: 'all', productIds: [], categoryIds: [] }
  }

  if (discount.appliesTo === 'product') {
    const productIds = extractIdsFromRelationship(discount.products)
    if (productIds.length === 0) return null
    return {
      discount,
      scope: 'product',
      productIds,
      categoryIds: [],
    }
  }

  if (discount.appliesTo === 'category') {
    const categoryIds = extractIdsFromRelationship(discount.category)
    if (categoryIds.length === 0) return null
    return {
      discount,
      scope: 'category',
      productIds: [],
      categoryIds,
    }
  }

  return null
}

export const getActiveDiscounts = cache(async (payload: Payload): Promise<Discount[]> => {
  const result = await payload.find({
    collection: 'discounts',
    where: buildActiveDiscountsWhere(),
    depth: 0,
    limit: 1000,
    pagination: false,
  })

  return result.docs as Discount[]
})
